import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { MapPin } from 'lucide-react';
import { api } from '@/lib/api';
import { useBranches } from '@/Pages/Operations/shared';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { SwitchRow } from '@/components/ui/switch';
import { ErrorState, LoadingState } from '@/components/ui/states';
import {
  GeofenceEnforcement,
  LocationCaptureMode,
  apiError,
  type AttendancePolicy,
  type UpdateAttendancePolicyInput,
} from './lib';

/**
 * The venue's attendance rules — and the one screen in this module that has
 * to say what it is turning on, not only what it is turning off.
 *
 * ADR-0008 §6: location capture and geofence enforcement are `OFF` by
 * default, and that default is the point — "do not require invasive
 * tracking by default". This screen never pre-selects `ON_PUNCH` or
 * `BLOCK`; every location control here is drawn even when it is off, with
 * the off state itself labelled, so a manager reading this screen with
 * nothing configured sees "location capture: off" rather than a blank where
 * a switch should be. Behind `settings.update`, not an `attendance.*` key —
 * turning on staff tracking is a venue-level decision, the same authority
 * that edits the guest transparency switches.
 */
export default function PolicyTab() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canEdit = can('settings.update');
  const branches = useBranches();

  const [branchId, setBranchId] = useState('');
  const [policy, setPolicy] = useState<AttendancePolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<UpdateAttendancePolicyInput>({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<AttendancePolicy>('/attendance/policy', { params: { branchId: branchId || undefined } })
      .then(({ data }) => {
        setPolicy(data);
        setDraft({});
      })
      .catch((e) => setError(apiError(e, t('workforce.policy.loadFailed'))))
      .finally(() => setLoading(false));
  }, [branchId, t]);

  useEffect(load, [load]);

  const effective = policy ? { ...policy, ...draft } : null;
  const dirty = Object.keys(draft).length > 0;

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await api.patch('/attendance/policy', { ...draft, branchId: branchId || undefined });
      toast.success(t('workforce.policy.saved'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('workforce.policy.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !policy) return <LoadingState label={t('common.loading')} />;
  if (error && !policy) return <ErrorState message={error} onRetry={load} />;
  if (!effective) return null;

  const locationOn = effective.locationCaptureMode === LocationCaptureMode.ON_PUNCH;
  const geofenceOn = effective.geofenceEnforcement !== GeofenceEnforcement.OFF;

  return (
    <div className="space-y-4">
      {branches.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <Field label={t('workforce.policy.scope')} hint={t('workforce.policy.scopeHint')}>
              <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">{t('workforce.policy.tenantDefault')}</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
          </CardContent>
        </Card>
      )}

      <Card className={locationOn ? 'border-amber-300 dark:border-amber-900' : undefined}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon as={MapPin} size="sm" />
            {t('workforce.policy.locationTitle')}
          </CardTitle>
          <p className="text-xs text-ink-muted">{t('workforce.policy.locationSubtitle')}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge tone={locationOn ? 'warning' : 'success'}>
              {t(locationOn ? 'workforce.policy.locationOnBadge' : 'workforce.policy.locationOffBadge')}
            </Badge>
            {!locationOn && <span className="text-xs text-ink-muted">{t('workforce.policy.defaultNote')}</span>}
          </div>

          <SwitchRow
            label={t('workforce.policy.captureLocation')}
            hint={t('workforce.policy.captureLocationHint')}
            checked={locationOn}
            disabled={!canEdit}
            onChange={(checked) =>
              setDraft((d) => ({
                ...d,
                locationCaptureMode: checked ? LocationCaptureMode.ON_PUNCH : LocationCaptureMode.OFF,
              }))
            }
          />

          <Field label={t('workforce.policy.geofenceEnforcement')} hint={t('workforce.policy.geofenceEnforcementHint')}>
            <Select
              value={effective.geofenceEnforcement}
              disabled={!canEdit || !locationOn}
              onChange={(e) =>
                setDraft((d) => ({ ...d, geofenceEnforcement: e.target.value as GeofenceEnforcement }))
              }
            >
              {Object.values(GeofenceEnforcement).map((g) => (
                <option key={g} value={g}>
                  {t(`workforce.geofenceEnforcement.${g}`)}
                </option>
              ))}
            </Select>
          </Field>

          {geofenceOn && (
            <Field label={t('workforce.policy.geofenceRadius')}>
              <Input
                type="number"
                min={10}
                max={50_000}
                disabled={!canEdit}
                value={effective.geofenceRadiusMeters}
                onChange={(e) => setDraft((d) => ({ ...d, geofenceRadiusMeters: Number(e.target.value) }))}
              />
            </Field>
          )}

          <Field
            label={t('workforce.policy.locationRetentionDays')}
            hint={t('workforce.policy.locationRetentionDaysHint')}
          >
            <Input
              type="number"
              min={1}
              max={365}
              disabled={!canEdit || !locationOn}
              value={effective.locationRetentionDays}
              onChange={(e) => setDraft((d) => ({ ...d, locationRetentionDays: Number(e.target.value) }))}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('workforce.policy.timeRulesTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <NumberField
            label={t('workforce.policy.lateGraceMinutes')}
            value={effective.lateGraceMinutes}
            disabled={!canEdit}
            onChange={(v) => setDraft((d) => ({ ...d, lateGraceMinutes: v }))}
          />
          <NumberField
            label={t('workforce.policy.earlyDepartureGraceMinutes')}
            value={effective.earlyDepartureGraceMinutes}
            disabled={!canEdit}
            onChange={(v) => setDraft((d) => ({ ...d, earlyDepartureGraceMinutes: v }))}
          />
          <NumberField
            label={t('workforce.policy.dailyOvertimeThresholdMinutes')}
            value={effective.dailyOvertimeThresholdMinutes}
            disabled={!canEdit}
            onChange={(v) => setDraft((d) => ({ ...d, dailyOvertimeThresholdMinutes: v }))}
          />
          <NumberField
            label={t('workforce.policy.weeklyOvertimeThresholdMinutes')}
            value={effective.weeklyOvertimeThresholdMinutes}
            disabled={!canEdit}
            onChange={(v) => setDraft((d) => ({ ...d, weeklyOvertimeThresholdMinutes: v }))}
          />
          <NumberField
            label={t('workforce.policy.autoCloseAfterMinutes')}
            value={effective.autoCloseAfterMinutes}
            disabled={!canEdit}
            onChange={(v) => setDraft((d) => ({ ...d, autoCloseAfterMinutes: v }))}
          />
          <SwitchRow
            label={t('workforce.policy.requireAuthorizedDevice')}
            checked={effective.requireAuthorizedDevice}
            disabled={!canEdit}
            onChange={(checked) => setDraft((d) => ({ ...d, requireAuthorizedDevice: checked }))}
          />
        </CardContent>
      </Card>

      {canEdit && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={!dirty} onClick={() => setDraft({})}>
            {t('common.cancel')}
          </Button>
          <Button disabled={!dirty || saving} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </div>
      )}
    </div>
  );
}

function NumberField({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <Field label={label}>
      <Input type="number" min={0} disabled={disabled} value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </Field>
  );
}
